import useSWRImmutable from "swr/immutable"
import { useCallback } from "react"
import { useSWRConfig } from "swr"
import { debug } from "@hornet-web-react/core/utils"
import {
  CookieName,
  NotificationsPermissionState,
} from "@hornet-web-react/core/types"
import { getCookie, setCookie } from "typescript-cookie"
import { AppConfig } from "@hornet-web-react/core/services/AppConfig"

const SWR_KEY = "/_services/memory_storage/feature_flags"

let currentPermissionQuery: PermissionStatus | undefined

export function useNotifications() {
  const cookieStorageFetcher = useCallback(
    () => getCookie(CookieName.NotificationsPermission),
    []
  )

  const { mutate } = useSWRConfig()
  const { data, isLoading } = useSWRImmutable(SWR_KEY, cookieStorageFetcher)

  const setPermissionState = useCallback(
    (newState: NotificationsPermissionState, appConfig: AppConfig) => {
      setCookie(
        CookieName.NotificationsPermission,
        newState,
        appConfig.simpleCookieConfig()
      )

      void mutate(SWR_KEY)
    },
    [mutate]
  )

  const requestPermission = useCallback(
    async (appConfig: AppConfig): Promise<NotificationsPermissionState> => {
      const result = await window.Notification.requestPermission()

      debug(`useNotifications: requestPermission result: ${result}`)

      const permissionState = result === "default" ? "prompt" : result
      void setPermissionState(permissionState, appConfig)

      return permissionState
    },
    [setPermissionState]
  )

  const detectCurrentPermission = useCallback(
    (appConfig: AppConfig) => {
      if ("permissions" in navigator && !currentPermissionQuery) {
        debug("useNotifications: finding out notifications permission")

        navigator.permissions
          .query({ name: "notifications" })
          .then((result) => {
            // we already did this
            if (currentPermissionQuery) {
              return
            }

            debug(
              `useNotifications: Notifications permission result.state: ${result.state}`
            )
            currentPermissionQuery = result

            // granted or denied can be safely considered as true
            // the default value is "prompt" on iOS even if it's "granted" (after reload)
            if (
              currentPermissionQuery.state === "granted" ||
              currentPermissionQuery.state === "denied"
            ) {
              setPermissionState(currentPermissionQuery.state, appConfig)
            }

            // this doesn't work on iOS so we never know it changed, have to workaround with polling
            //  see: https://developer.apple.com/forums/thread/757353
            //  see: https://developer.apple.com/forums/thread/742439
            currentPermissionQuery.onchange = () => {
              debug(
                `useNotifications: Notifications permission changed: ${currentPermissionQuery?.state}`
              )
              if (currentPermissionQuery) {
                debug(
                  `useNotifications: Notifications permission changed setting state: ${currentPermissionQuery.state}`
                )
                setPermissionState(currentPermissionQuery.state, appConfig)
              }
            }
          })
          .catch((e: unknown) => {
            setPermissionState("error", appConfig)
            console.error("useNotifications: Error updating the permissions", e)
          })
      }
    },
    [setPermissionState]
  )

  const permissionState = NotificationsPermissionState.safeParse(data)

  return {
    isEnabled: permissionState.success && permissionState.data === "granted",

    permissionState: isLoading
      ? undefined
      : permissionState.success
      ? permissionState.data
      : "unknown",

    setPermissionState,

    requestPermission,
    detectCurrentPermission,
  }
}
